// ============================================================================
// BUILD WITH AI PAGE
// 3-step wizard for generating scaffold bundles from session context.
// Can be used standalone at /build or embedded in ProjectDetail.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Hammer,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  CheckCircle,
  Circle,
  Loader,
  FolderOpen,
  FileText,
  Users,
  ClipboardList,
  Settings,
  Download,
  AlertCircle,
  Sparkles,
  Package,
  TestTube,
  Cloud,
  Palette,
  Zap,
  FileCheck,
} from 'lucide-react';
import { api } from '../services/api';
import {
  projectTasksService,
  CreateProjectTaskInput,
  ProjectTaskSummary
} from '../services/sessionTasksService';

// ============================================================================
// Types
// ============================================================================

interface BuildWithAIProps {
  projectId?: string;
  embedded?: boolean;
}

type WizardStep = 'source_context' | 'config' | 'review';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'source_context', label: '1. Source & Context' },
  { id: 'config', label: '2. Configure Build' },
  { id: 'review', label: '3. Review & Launch' },
];

const STEP_SUBTITLES: Record<WizardStep, string> = {
  source_context: 'Select Project & Session Context',
  config: 'Configure Build, Skills & Designs',
  review: 'Review & Launch',
};

interface ProjectData {
  projectId: string;
  name: string;
  description?: string;
  status?: string;
  sessionCount?: number;
}

interface SessionData {
  sessionId: string;
  customerName?: string;
  name?: string;
  status?: string;
}

interface SessionContext {
  transcripts: Array<{ id: string; timestamp: string; role: string; preview: string }>;
  requirements: Array<{ id: string; title: string; description: string }>;
  personas: Array<{ id: string; name: string; role: string }>;
  avatars: Array<{ id: string; name: string; role: string }>;
}

interface S3File {
  key: string;
  name: string;
  size: number;
  lastModified: string;
}

interface SkillMeta {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
}

interface SkillBundle {
  id: string;
  name: string;
  description: string;
  skillIds: string[];
}

// Category icon mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Testing: <TestTube size={14} />,
  DevOps: <Cloud size={14} />,
  Design: <Palette size={14} />,
  Engineering: <Zap size={14} />,
  Quality: <FileCheck size={14} />,
};

// Build status -> badge class mapping
function badgeClassForStatus(status: string): string {
  if (status === 'running') return 'bg-blue-100 text-blue-700';
  if (status === 'failed') return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-600';
}

// ============================================================================
// Component
// ============================================================================

export default function BuildWithAI({ projectId: propProjectId, embedded }: BuildWithAIProps) {
  const [searchParams] = useSearchParams();

  // Steps (skip source_context step when embedded with projectId — just show sessions)
  const initialStep: WizardStep = 'source_context';

  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<{ taskId: string } | null>(null);

  // Source (project selection)
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    propProjectId || searchParams.get('projectId') || ''
  );

  // Context (sessions)
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [sessionContexts, setSessionContexts] = useState<Record<string, SessionContext>>({});
  const [loadingSessionContext, setLoadingSessionContext] = useState<string | null>(null);
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<string[]>([]);
  const [selectedRequirementIds, setSelectedRequirementIds] = useState<string[]>([]);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);

  // Config
  const [techStack, setTechStack] = useState('react');
  const [cssFramework, setCssFramework] = useState('tailwind');
  const [repoName, setRepoName] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  // Skills
  const [skills, setSkills] = useState<SkillMeta[]>([]);
  const [bundles, setBundles] = useState<SkillBundle[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [smartDefaultIds, setSmartDefaultIds] = useState<string[]>([]);
  const [smartDefaultApplied, setSmartDefaultApplied] = useState(false);

  // Build History
  const [builds, setBuilds] = useState<ProjectTaskSummary[]>([]);
  const [buildsLoading, setBuildsLoading] = useState(false);
  const [downloadingTaskId, setDownloadingTaskId] = useState<string | null>(null);

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  const selectedProject = projects.find(p => p.projectId === selectedProjectId);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const response = await api.get('/api/projects');
      setProjects(response.data.projects || []);
    } catch (err: any) {
      setError('Failed to load projects: ' + err.message);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoadingSessions(true);
    try {
      const response = await api.get(`/api/projects/${selectedProjectId}/sessions`);
      setSessions(response.data.sessions || []);
    } catch (err: any) {
      setError('Failed to load sessions: ' + err.message);
    } finally {
      setLoadingSessions(false);
    }
  }, [selectedProjectId]);

  const loadSessionContext = async (sessionId: string) => {
    if (sessionContexts[sessionId]) return;
    setLoadingSessionContext(sessionId);
    try {
      const response = await api.get(`/api/sessions/${sessionId}/context`);
      const ctx = response.data;
      setSessionContexts(prev => ({
        ...prev,
        [sessionId]: {
          transcripts: ctx.transcriptEntries || [],
          requirements: ctx.requirements || [],
          personas: ctx.personas || [],
          avatars: ctx.sessionAvatars || []
        }
      }));
    } catch {
      setSessionContexts(prev => ({
        ...prev,
        [sessionId]: { transcripts: [], requirements: [], personas: [], avatars: [] }
      }));
    } finally {
      setLoadingSessionContext(null);
    }
  };


  const loadSkills = useCallback(async () => {
    setLoadingSkills(true);
    try {
      const response = await api.get('/api/skills');
      setSkills(response.data.skills || []);
      setBundles(response.data.bundles || []);
    } catch {
      setSkills([]);
      setBundles([]);
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  const loadBuilds = useCallback(async () => {
    if (!selectedProjectId) return;
    setBuildsLoading(true);
    try {
      const tasks = await projectTasksService.listTasks(selectedProjectId);
      const buildTasks = tasks
        .filter(t => t.templateId === 'session-task-build-with-ai')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setBuilds(buildTasks);
    } catch (err) {
      console.error('Failed to load builds:', err);
    } finally {
      setBuildsLoading(false);
    }
  }, [selectedProjectId]);

  const handleDownloadBuild = async (taskId: string) => {
    if (!selectedProjectId) return;
    setDownloadingTaskId(taskId);
    try {
      const detail = await projectTasksService.getTask(selectedProjectId, taskId);
      const scaffoldOutput = detail.outputs?.find((o: any) => o.type === 'scaffold_bundle');
      if (!scaffoldOutput?.content) {
        throw new Error('No scaffold bundle found');
      }
      const parsed = typeof scaffoldOutput.content === 'string'
        ? JSON.parse(scaffoldOutput.content)
        : scaffoldOutput.content;
      if (!parsed.s3Key) {
        throw new Error('No scaffold bundle S3 key found');
      }
      const s3Key = parsed.s3Key;
      const projId = s3Key.split('/')[1];
      const response = await api.get(
        `/api/projects/${projId}/s3/download?key=${encodeURIComponent(s3Key)}`
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (err: any) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingTaskId(null);
    }
  };

  // Load projects on mount (standalone mode)
  useEffect(() => {
    if (!propProjectId && !embedded) {
      loadProjects();
    }
  }, [propProjectId, embedded, loadProjects]);

  // Load sessions when project is selected
  useEffect(() => {
    if (selectedProjectId && sessions.length === 0) {
      loadSessions();
    }
  }, [selectedProjectId, sessions.length, loadSessions]);

  // Load skills when entering config step
  useEffect(() => {
    if (currentStep === 'config' && selectedProjectId) {
      loadSkills();
    }
  }, [currentStep, selectedProjectId, loadSkills]);

  // Load build history when project is selected
  useEffect(() => {
    if (selectedProjectId) {
      loadBuilds();
    }
  }, [selectedProjectId, loadBuilds]);

  // Poll for builds every 15s while any build is running
  useEffect(() => {
    const hasRunning = builds.some(b => b.status === 'running' || b.status === 'pending');
    if (!hasRunning || !selectedProjectId) return;
    const interval = setInterval(loadBuilds, 15000);
    return () => clearInterval(interval);
  }, [builds, selectedProjectId, loadBuilds]);

  // Auto-generate repo name from project
  useEffect(() => {
    if (selectedProject && !repoName) {
      const slug = selectedProject.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(?:^-+|-+$)/g, '');
      setRepoName(slug + '-prototype');
    }
  }, [selectedProject, repoName]);

  // Smart defaults for skills
  useEffect(() => {
    if (skills.length === 0 || smartDefaultApplied) return;
    const stackTags = [techStack];
    if (cssFramework && cssFramework !== 'none') stackTags.push(cssFramework);

    const defaultIds = skills
      .filter(s => s.tags.some(t => stackTags.includes(t)))
      .map(s => s.id);

    // Universal defaults
    if (!defaultIds.includes('code-review-checklist')) defaultIds.push('code-review-checklist');
    if (!defaultIds.includes('documentation-generation')) defaultIds.push('documentation-generation');

    setSelectedSkillIds(defaultIds);
    setSmartDefaultIds(defaultIds);
    setSmartDefaultApplied(true);
  }, [skills, techStack, cssFramework, smartDefaultApplied]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const toggleSession = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
        loadSessionContext(sessionId);
      }
      return next;
    });
  };

  const toggleSessionSelection = (sessionId: string) => {
    if (selectedSessionIds.includes(sessionId)) {
      setSelectedSessionIds(prev => prev.filter(id => id !== sessionId));
    } else {
      setSelectedSessionIds(prev => [...prev, sessionId]);
      if (!expandedSessions.has(sessionId)) {
        toggleSession(sessionId);
      }
    }
  };

  const toggleItem = (id: string, list: string[], setList: (v: string[]) => void) => {
    if (list.includes(id)) {
      setList(list.filter(i => i !== id));
    } else {
      setList([...list, id]);
    }
  };

  const toggleSkill = (id: string) => {
    setSelectedSkillIds(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleBundle = (bundle: SkillBundle) => {
    const allSelected = bundle.skillIds.every(id => selectedSkillIds.includes(id));
    if (allSelected) {
      setSelectedSkillIds(prev => prev.filter(id => !bundle.skillIds.includes(id)));
    } else {
      setSelectedSkillIds(prev => {
        const next = new Set(prev);
        bundle.skillIds.forEach(id => next.add(id));
        return Array.from(next);
      });
    }
  };

  const handleNext = () => {
    const idx = STEPS.findIndex(s => s.id === currentStep);
    if (idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1].id);
    }
  };

  const handleBack = () => {
    const idx = STEPS.findIndex(s => s.id === currentStep);
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1].id);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setError(null);

    try {
      // Auto-include session IDs for any sessions that have selected child items
      const effectiveSessionIds = new Set(selectedSessionIds);
      for (const [sessionId, ctx] of Object.entries(sessionContexts)) {
        const hasSelectedTranscripts = ctx.transcripts.some(t => selectedTranscriptIds.includes(t.id));
        const hasSelectedRequirements = ctx.requirements.some(r => selectedRequirementIds.includes(r.id));
        const hasSelectedPersonas = ctx.personas.some(p => selectedPersonaIds.includes(p.id));
        if (hasSelectedTranscripts || hasSelectedRequirements || hasSelectedPersonas) {
          effectiveSessionIds.add(sessionId);
        }
      }

      const input: CreateProjectTaskInput = {
        taskTemplateId: 'session-task-build-with-ai',
        taskName: `Build with AI: ${repoName}`,
        taskCategory: 'doing',
        contextSnapshot: {
          transcriptEntryIds: selectedTranscriptIds,
          requirementIds: selectedRequirementIds,
          personaIds: selectedPersonaIds,
          customContext: {
            mode: 'download_bundle',
            selectedSkillIds,
            buildConfig: {
              techStack,
              cssFramework,
              repoName,
              additionalInstructions: additionalInstructions.trim() || undefined
            }
          }
        },
        sourceSessionIds: Array.from(effectiveSessionIds)
      };

      const result = await projectTasksService.createTask(selectedProjectId, input);
      setSubmitSuccess(result);
      loadBuilds();
      // Retry load after 2s in case of DynamoDB eventual consistency
      setTimeout(() => loadBuilds(), 2000);
      // Auto-reset wizard after 3s
      setTimeout(() => {
        setSubmitSuccess(null);
        setCurrentStep(initialStep);
        setSelectedSessionIds([]);
        setSelectedTranscriptIds([]);
        setSelectedRequirementIds([]);
        setSelectedPersonaIds([]);
        setRepoName('');
        setAdditionalInstructions('');
        setSelectedSkillIds(smartDefaultIds);
      }, 3000);
    } catch (err: any) {
      setError('Failed to launch build: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contextItemCount =
    selectedTranscriptIds.length +
    selectedRequirementIds.length +
    selectedPersonaIds.length;

  const isNextDisabled =
    (currentStep === 'source_context' && !selectedProjectId) ||
    (currentStep === 'config' && !repoName.trim()) ||
    isSubmitting;

  // Group skills by category
  const skillsByCategory = skills.reduce<Record<string, SkillMeta[]>>((acc, skill) => {
    if (!acc[skill.category]) acc[skill.category] = [];
    acc[skill.category].push(skill);
    return acc;
  }, {});

  // ============================================================================
  // Render
  // ============================================================================

  function renderProjectsListContent(): React.ReactNode {
    if (loadingProjects) {
      return (
        <div className="text-center py-10 text-slate-500">
          <Loader size={24} className="animate-spin mx-auto mb-3" />
          Loading projects...
        </div>
      );
    }
    if (projects.length === 0) {
      return (
        <div className="text-center py-10 text-slate-400">
          <FolderOpen size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No projects found</p>
          <p className="text-sm mt-1">Create a project first to get started.</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.filter(p => p.status !== 'archived').map(project => {
          const isSelected = selectedProjectId === project.projectId;
          return (
            <div
              key={project.projectId}
              onClick={() => {
                setSelectedProjectId(project.projectId);
                setSessions([]);
              }}
              className={`relative flex flex-col p-5 rounded-xl cursor-pointer transition-all ${
                isSelected
                  ? 'border-2 border-primary bg-primary/5'
                  : 'border border-slate-200 bg-white hover:border-primary/50 hover:bg-primary/5'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <FolderOpen size={20} className={isSelected ? 'text-primary' : 'text-slate-400'} />
                <h3 className="font-bold text-slate-900">{project.name}</h3>
              </div>
              {project.description && (
                <p className="text-sm text-slate-500 mb-2 line-clamp-2">{project.description}</p>
              )}
              <div className="text-xs text-slate-400 mt-auto">
                {project.sessionCount ?? 0} session{(project.sessionCount ?? 0) !== 1 ? 's' : ''}
              </div>
              {isSelected && (
                <div className="absolute top-4 right-4 text-primary">
                  <CheckCircle size={20} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderSessionsListContent(): React.ReactNode {
    if (loadingSessions) {
      return (
        <div className="text-center py-10 text-slate-500">
          <Loader size={24} className="animate-spin mx-auto mb-3" />
          Loading sessions...
        </div>
      );
    }
    if (sessions.length === 0) {
      return (
        <div className="text-center py-10 text-slate-400">
          No sessions linked to this project yet.
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {sessions.map(session => {
          const sessionId = session.sessionId;
          const isSelected = selectedSessionIds.includes(sessionId);
          const isExpanded = expandedSessions.has(sessionId);
          const ctx = sessionContexts[sessionId];
          const isLoadingCtx = loadingSessionContext === sessionId;

          let statusDotClass: string;
          if (session.status === 'active') {
            statusDotClass = 'bg-green-500';
          } else if (session.status === 'ended') {
            statusDotClass = 'bg-slate-400';
          } else {
            statusDotClass = 'bg-amber-400';
          }

          let sessionContextBody: React.ReactNode = null;
          if (isLoadingCtx) {
            sessionContextBody = (
              <div className="text-center py-4 text-slate-400 text-sm">
                <Loader size={16} className="animate-spin inline mr-2" />
                Loading context...
              </div>
            );
          } else if (ctx) {
            sessionContextBody = (
              <div className="space-y-4">
                {/* Personas */}
                {ctx.personas.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      <Users size={12} className="inline mr-1" />
                      Personas ({ctx.personas.filter(p => selectedPersonaIds.includes(p.id)).length}/{ctx.personas.length})
                    </label>
                    <div className="space-y-1">
                      {ctx.personas.map(persona => (
                        <label key={persona.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                          <input
                            type="checkbox"
                            checked={selectedPersonaIds.includes(persona.id)}
                            onChange={() => toggleItem(persona.id, selectedPersonaIds, setSelectedPersonaIds)}
                            className="rounded border-slate-300 text-primary focus:ring-primary/30"
                          />
                          <span className="font-medium">{persona.name}</span>
                          <span className="text-slate-400">— {persona.role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Requirements */}
                {ctx.requirements.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      <ClipboardList size={12} className="inline mr-1" />
                      Requirements ({ctx.requirements.filter(r => selectedRequirementIds.includes(r.id)).length}/{ctx.requirements.length})
                    </label>
                    <div className="space-y-1">
                      {ctx.requirements.map(req => (
                        <label key={req.id} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                          <input
                            type="checkbox"
                            checked={selectedRequirementIds.includes(req.id)}
                            onChange={() => toggleItem(req.id, selectedRequirementIds, setSelectedRequirementIds)}
                            className="rounded border-slate-300 text-primary focus:ring-primary/30 mt-0.5"
                          />
                          <span>{req.title || req.description}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transcripts */}
                {ctx.transcripts.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      <FileText size={12} className="inline mr-1" />
                      Transcript Entries ({ctx.transcripts.filter(t => selectedTranscriptIds.includes(t.id)).length}/{ctx.transcripts.length})
                    </label>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {ctx.transcripts.slice(0, 50).map(entry => (
                        <label key={entry.id} className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-900">
                          <input
                            type="checkbox"
                            checked={selectedTranscriptIds.includes(entry.id)}
                            onChange={() => toggleItem(entry.id, selectedTranscriptIds, setSelectedTranscriptIds)}
                            className="rounded border-slate-300 text-primary focus:ring-primary/30 mt-0.5"
                          />
                          <span className="truncate">{entry.preview || entry.role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {ctx.personas.length === 0 && ctx.requirements.length === 0 && ctx.transcripts.length === 0 && (
                  <p className="text-sm text-slate-400">No context items found for this session.</p>
                )}
              </div>
            );
          }

          return (
            <div key={sessionId} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSessionSelection(sessionId)}
                  className="rounded border-slate-300 text-primary focus:ring-primary/30"
                />
                <div className="flex-1 flex items-center gap-2" onClick={() => toggleSession(sessionId)}>
                  {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                  <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotClass}`} />
                  <span className="text-sm font-medium text-slate-900">
                    {session.customerName || session.name || sessionId}
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                  {sessionContextBody}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  let submitButtonContent: React.ReactNode;
  if (isSubmitting) {
    submitButtonContent = (
      <>
        <Loader size={16} className="animate-spin" />
        Generating...
      </>
    );
  } else {
    submitButtonContent = (
      <>
        <Download size={16} />
        Generate Bundle
      </>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-surface-light'}>
      <div className={`${embedded ? '' : 'max-w-4xl mx-auto pt-8 px-6 pb-12'}`}>
        {/* Header (standalone mode only) */}
        {!embedded && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Hammer className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Build with AI</h1>
                <p className="text-sm text-slate-500">Generate a scaffold bundle for Claude Code</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {STEPS.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            let stepClass: string;
            if (isActive) {
              stepClass = 'bg-primary text-white font-semibold shadow-sm';
            } else if (isCompleted) {
              stepClass = 'bg-emerald-500 text-white font-semibold';
            } else {
              stepClass = 'bg-slate-100 text-slate-500 font-medium';
            }
            return (
              <div
                key={step.id}
                className={`flex h-9 items-center justify-center gap-x-2 rounded-full px-4 text-sm transition-colors ${stepClass}`}
              >
                {isCompleted ? <CheckCircle size={16} /> : <Circle size={16} />}
                <span>{step.label}</span>
              </div>
            );
          })}
        </div>

        {/* Step subtitle */}
        <p className="text-slate-500 text-sm mb-6">
          Step {currentStepIndex + 1}: {STEP_SUBTITLES[currentStep]}
        </p>

        {/* Success Banner */}
        {submitSuccess && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle size={20} className="text-emerald-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-900">Build launched successfully!</p>
              <p className="text-xs text-emerald-600">Track progress in Build History below.</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg mb-6 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {/* Content Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mb-6">

          {/* ========== STEP 1: Source & Context ========== */}
          {currentStep === 'source_context' && (
            <div className="space-y-8">
              {/* Project Selection (standalone mode) */}
              {!propProjectId && !embedded && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FolderOpen size={16} className="text-primary" />
                    Select Project
                  </h3>
                  {renderProjectsListContent()}
                </div>
              )}

              {/* Session Context Selection */}
              {selectedProjectId && (
                <div>
                  {!propProjectId && !embedded && (
                    <div className="border-t border-slate-200 pt-6" />
                  )}
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FileText size={16} className="text-primary" />
                    Session Context
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-4">
                    Select sessions and their context items to include in the scaffold bundle. Expand a session to pick specific items.
                  </div>

                  {renderSessionsListContent()}
                </div>
              )}
            </div>
          )}
          {/* ========== STEP 2: Configure Build ========== */}
          {currentStep === 'config' && (
            <div className="space-y-10">

              {/* --- Section: Build Configuration --- */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Settings size={16} className="text-primary" />
                  Build Configuration
                </h3>
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Tech Stack</label>
                      <select
                        value={techStack}
                        onChange={(e) => setTechStack(e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      >
                        <option value="react">React</option>
                        <option value="nextjs">Next.js</option>
                        <option value="vue">Vue.js</option>
                        <option value="svelte">Svelte</option>
                        <option value="python-fastapi">Python / FastAPI</option>
                        <option value="python-flask">Python / Flask</option>
                        <option value="node-express">Node.js / Express</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">CSS Framework</label>
                      <select
                        value={cssFramework}
                        onChange={(e) => setCssFramework(e.target.value)}
                        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      >
                        <option value="tailwind">Tailwind CSS</option>
                        <option value="material-ui">Material UI</option>
                        <option value="shadcn">shadcn/ui</option>
                        <option value="chakra">Chakra UI</option>
                        <option value="bootstrap">Bootstrap</option>
                        <option value="none">None / Custom</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Repository Name</label>
                    <input
                      type="text"
                      value={repoName}
                      onChange={(e) => setRepoName(e.target.value)}
                      placeholder="e.g., customer-prototype"
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                    <p className="text-xs text-slate-400 mt-1">Used as the folder name in the scaffold bundle.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Additional Instructions (optional)</label>
                    <textarea
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      placeholder="Any specific instructions for Claude Code, e.g., 'Focus on mobile-first design', 'Include authentication flow'..."
                      rows={3}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-[inherit] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* --- Section: Claude Code Skills --- */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Sparkles size={16} className="text-primary" />
                  Claude Code Skills
                  <span className="text-xs font-normal text-slate-400 normal-case tracking-normal">
                    ({selectedSkillIds.length} selected)
                  </span>
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Skills become <code className="bg-slate-100 px-1 rounded">/slash-commands</code> in Claude Code. Recommended skills are pre-selected based on your tech stack.
                </p>

                {loadingSkills ? (
                  <div className="text-center py-6 text-slate-400">
                    <Loader size={16} className="animate-spin mx-auto mb-2" />
                    Loading skills...
                  </div>
                ) : (
                  <>
                    {/* Bundle quick-select chips */}
                    {bundles.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-5">
                        {bundles.map(bundle => {
                          const allSelected = bundle.skillIds.every(id => selectedSkillIds.includes(id));
                          return (
                            <button
                              key={bundle.id}
                              onClick={() => toggleBundle(bundle)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                allSelected
                                  ? 'bg-primary text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              title={bundle.description}
                            >
                              <Package size={12} />
                              {bundle.name}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Skills grouped by category */}
                    <div className="space-y-5">
                      {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
                        <div key={category}>
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-slate-400">{CATEGORY_ICONS[category] || <Sparkles size={14} />}</span>
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{category}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {categorySkills.map(skill => {
                              const isSelected = selectedSkillIds.includes(skill.id);
                              const isRecommended = smartDefaultIds.includes(skill.id);
                              return (
                                <label
                                  key={skill.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                                    isSelected
                                      ? 'border-primary/30 bg-primary/5'
                                      : 'border-slate-200 bg-white hover:border-slate-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleSkill(skill.id)}
                                    className="rounded border-slate-300 text-primary focus:ring-primary/30 mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-slate-900">{skill.name}</span>
                                      {isRecommended && (
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary uppercase">
                                          Recommended
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{skill.description}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

            </div>
          )}

          {/* ========== STEP 3: Review & Launch ========== */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <h3 className="font-bold text-slate-900 text-lg">Review Your Build</h3>

              <div className="space-y-4">
                {/* Project */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <FolderOpen size={18} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</span>
                    <p className="text-sm text-slate-900 font-medium">{selectedProject?.name || selectedProjectId}</p>
                  </div>
                </div>

                {/* Context */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <FileText size={18} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Context</span>
                    <p className="text-sm text-slate-900">
                      {selectedSessionIds.length} session{selectedSessionIds.length !== 1 ? 's' : ''} ·{' '}
                      {contextItemCount} item{contextItemCount !== 1 ? 's' : ''} selected
                      <span className="text-slate-400 ml-1">
                        ({selectedPersonaIds.length} personas, {selectedRequirementIds.length} requirements, {selectedTranscriptIds.length} transcripts)
                      </span>
                    </p>
                  </div>
                </div>

                {/* Config */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Settings size={18} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Configuration</span>
                    <div className="text-sm text-slate-900 space-y-1 mt-1">
                      <p><strong>Tech Stack:</strong> {techStack}</p>
                      <p><strong>CSS Framework:</strong> {cssFramework}</p>
                      <p><strong>Repo Name:</strong> {repoName}</p>
                      {additionalInstructions && (
                        <p><strong>Instructions:</strong> {additionalInstructions}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Skills */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Sparkles size={18} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Claude Code Skills</span>
                    <p className="text-sm text-slate-900">
                      {selectedSkillIds.length} skill{selectedSkillIds.length !== 1 ? 's' : ''} selected
                    </p>
                    {selectedSkillIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {selectedSkillIds.map(id => {
                          const skill = skills.find(s => s.id === id);
                          return skill ? (
                            <span key={id} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-medium">
                              {skill.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentStepIndex === 0 || isSubmitting}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              currentStepIndex === 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <ChevronLeft size={16} />
            Back
          </button>

          {currentStep === 'review' ? (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-white hover:bg-primary/90 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {submitButtonContent}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={isNextDisabled}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white hover:bg-primary/90 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* Build History */}
        {selectedProjectId && (
          <div className="mt-8">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Hammer size={20} className="text-primary" />
              Build History
            </h3>
            {renderBuildHistoryContent()}
          </div>
        )}
      </div>
    </div>
  );

  function renderBuildHistoryContent(): React.ReactNode {
    if (buildsLoading && builds.length === 0) {
      return (
        <div className="text-center py-8 text-slate-400">
          <Loader size={20} className="animate-spin mx-auto mb-2" />
          Loading builds...
        </div>
      );
    }
    if (builds.length === 0) {
      return (
        <div className="text-center py-8 text-slate-400 text-sm">
          No builds yet. Configure and launch your first build above.
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {builds.map(build => {
                  let statusBgClass: string;
                  let statusIcon: React.ReactNode;
                  if (build.status === 'completed') {
                    statusBgClass = 'bg-emerald-100';
                    statusIcon = <CheckCircle size={20} className="text-emerald-600" />;
                  } else if (build.status === 'running') {
                    statusBgClass = 'bg-blue-100';
                    statusIcon = <Loader size={20} className="text-blue-600 animate-spin" />;
                  } else if (build.status === 'failed') {
                    statusBgClass = 'bg-red-100';
                    statusIcon = <AlertCircle size={20} className="text-red-500" />;
                  } else {
                    statusBgClass = 'bg-slate-100';
                    statusIcon = <Circle size={20} className="text-slate-400" />;
                  }

                  return (
                    <div key={build.taskId} className="bg-white rounded-lg border border-slate-200">
                      <div className="p-4 flex items-center gap-4">
                        {/* Status indicator */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${statusBgClass}`}>
                          {statusIcon}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {build.taskName}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(build.createdAt).toLocaleString()}
                            {build.status === 'running' && ' · Building...'}
                            {build.sourceSessionName && ` · ${build.sourceSessionName}`}
                          </p>
                        </div>
                        {/* Actions */}
                        {build.status === 'completed' && (
                          <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDownloadBuild(build.taskId)}
                                disabled={downloadingTaskId === build.taskId}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 transition-colors"
                              >
                                {downloadingTaskId === build.taskId ? (
                                  <Loader size={14} className="animate-spin" />
                                ) : (
                                  <Download size={14} />
                                )}
                                Download
                              </button>
                          </div>
                        )}
                        {/* Status badge */}
                        {build.status !== 'completed' && (
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badgeClassForStatus(build.status)}`}>
                            {build.status}
                          </span>
                        )}
                      </div>

                    </div>
                  );
                })}
      </div>
    );
  }
}
